import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components/native";
import { Input } from "@components/atoms";
import {
  NavigationProp,
  useLinkTo,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import {
  DeviceEventEmitter,
  KeyboardAvoidingView,
  Platform,
  View,
  ScrollView,
  Pressable,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTextSearchResult } from "@apis/textSearchResult";
import { TextSearchData } from "@src/types/textSearchResult";
import Loader from "@components/atoms/Loader";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { navigate } from "@navigations/utils/RootNavigation";
import { schema } from "@scenes/favorites/utils/collectionSchema";
import Routes from "@navigations/Routes";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { RootStackParams } from "@navigations/explore-navigator";
import { ProductCategory, ScreenName } from "@utils/constants";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { VDBIcons } from "@utils/VDBIcons";
import { setBuilderProduct, setProductType } from "@redux/slices/appStateSlice";
import { Tooltip } from "@gluestack-ui/themed";
import { TooltipContent } from "@gluestack-ui/themed";
import AppProviders from "@components/common/AppProviders";
import { useDebounce } from "use-debounce";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import { extractWidthValue } from "@components/common/Header/utils";
import { Section, TextSearchScreenProps } from "./types";
import { NoMatchesFound, NoRecentSearch } from "./NoSearch";
import {
  hasLabGrownJewelry,
  productTypeToCategory,
  selectedSearchGroups,
} from "./utils";
import { SearchResults } from "./SearchResults";
import {
  clearCollections,
  clearSelectedCollectionOption,
  clearSubCollections,
  resetFiltersPayload,
  resetJewelryFiltersPayload,
  setCollection,
  setFiltersPayload,
  setFiltersSet,
  setPayloadData,
  setSectionName,
  useSearchFiltersAuxPayload,
} from "@redux/slices/searchFilters";
import { useDispatch, useSelector } from "react-redux";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useProductPrefix } from "../../hooks/useProductPrefix";
import { TextInput } from "react-native-gesture-handler";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useNavigationHistory } from "../../hooks/useNavigationHistory";
import {
  BreadCrumbLabel,
  NavigationHistoryState,
} from "@components/molecules/Breadcrumbs/utils";
import { handleFilters } from "@components/atoms/Filters/ButtonSection/Utils";
import { commaFormattedValue } from "@components/atoms/Filters/FilterSection/Utils";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { StoneType } from "@scenes/bulk-request/Utils";
import styled from "styled-components/native";
import BarcodeButton from "@components/molecules/BarcodeButton";
import { useBrandsList } from "@apis/useBrandsList";
import { VDBText } from "@components/atoms";
import TextSearchProductType from "@components/atoms/TextSearch/TextSearchProductType";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { getSectionName } from "@utils/product/searchResultsNavigation";
import {
  useAppSettings,
  useConsolidatedStoneTypes,
} from "@redux/slices/globalSlice";
import { RedirectFunc } from "@scenes/configurator/OrderCart/utils";
import { fallbackIfEmptyDeeplink } from "@scenes/favorites/utils/FavoritesUtils";
import {
  BuilderAlertManager,
  shouldConfirmHeaderNavigation,
} from "@scenes/jewelry-builder/components/BuilderAlertManager";
import { useGetScreenData } from "../../hooks/useGetScreenData";
import { getDefaultScreenConfig } from "@navigations/utils/convertTabsData";

const NUMBER_OF_RECORDS_TO_AGGREGATE = Infinity;
const MAX_WIDTH = 424;

const TextSearchScreen = ({
  showResultsAsTooltip,
  onBackPress,
  customWidth,
  textSearchInputValue,
  setTextSearchInputValue,
}: TextSearchScreenProps) => {
  const [inputValueState, setInputValueState] = useState("");
  const navigation = useNavigation<NavigationProp<RootStackParams>>();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { addRouteToHistory, clearRouteHistory } = useNavigationHistory();
  const { control } = useForm({
    resolver: yupResolver(schema),
  });
  const dispatch = useDispatch();
  const linkTo = useLinkTo();
  const [searchWidth, setSearchWidth] = useState<number | null>(
    extractWidthValue(customWidth) ?? null
  );
  const [data, setData] = useState<Section[] | null>(null);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [focussedItem, setFocusedItem] = useState(0);
  let auxPayload = useSearchFiltersAuxPayload();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const jewelryWithId =
    userInfo?.user?.feature_settings?.allow_stock_num_search_by_brand;

  const getAppSettings = useAppSettings();
  const noDataDeeplink = getAppSettings?.searchNowDeepLink ?? "";
  const theme = useTheme();
  const route = useRoute();
  const inputRef = useRef<TextInput>(null);
  const routeProductType =
    (route.params as { productType?: string } | undefined)?.productType ?? "";
  // Header embeds this screen with showResultsAsTooltip; useRoute() still reflects
  // the focused screen (e.g. FilterScreen) and would leak productType otherwise.
  const productType = showResultsAsTooltip ? "" : routeProductType;
  const { selectedGroups } = useSearchGroupSlice();
  const result = selectedSearchGroups(selectedGroups);
  const finalResult = Object.assign({}, ...result);
  const typography = useTypographyStore();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const productPrefix = useProductPrefix();
  const allowedProducts = useSelector(
    (state: any) => state.globalData?.products
  );
  const tabsData = useGetScreenData(ScreenName.TABS);
  const defaultScreenConfig = getDefaultScreenConfig(tabsData) ?? {};
  const globalData = useSelector((state: any) => state.globalData);
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const disableBarcodeScanner =
    !globalData?.barcode_scanner?.enabled ||
    !globalData?.barcode_scanner?.price_modes.includes(
      userInfo?.user?.price_mode
    ) ||
    Platform.OS === "web";
  const selectCollectionFromTextSearch = useSelector(
    (state: any) =>
      state.globalData?.app_settings?.select_collection_from_text_search
  );
  const inputValue = textSearchInputValue ?? inputValueState;
  const [debouncedInputValue] = useDebounce(inputValue, 400);
  const consolidatedStoneTypes = useConsolidatedStoneTypes();
  const productSubType = consolidatedStoneTypes?.enabled ? "all" : undefined;

  const setInputValue = (value: string) => {
    setTextSearchInputValue?.(value);
    setInputValueState?.(value);
  };

  const handleLeftIconPress = (clearInput: boolean) => {
    if (showResultsAsTooltip) {
      onBackPress?.(clearInput);
    } else {
      if (inputValue.length === 0) {
        navigation.goBack();
      } else {
        setInputValue("");
        setData([]);
        if (jewelryWithId) {
          setBrandSearch("");
        }
      }
    }
  };

  const onRemoveFilters = () => {
    if (
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY
    ) {
      DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
      dispatch(resetJewelryFiltersPayload({}));
    } else {
      DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
      dispatch(resetFiltersPayload());
    }
  };

  const handleOnPressExploreProducts = () => {
    if (noDataDeeplink && noDataDeeplink?.length > 0) {
      RedirectFunc(noDataDeeplink, linkTo);
    } else {
      fallbackIfEmptyDeeplink(userInfo, dispatch, theme);
    }
  };

  const handleDidSelectItem = (selectedItem: TextSearchData) => {
    const productSearchType =
      selectedItem.product === 0
        ? ProductCategory.DIAMOND
        : selectedItem.product === 1
        ? ProductCategory.LAB_GROWN_DIAMOND
        : selectedItem.product === 2
        ? ProductCategory.GEMSTONE
        : selectedItem.product === 3
        ? ProductCategory.JEWELRY
        : ProductCategory.LABGROWN_JEWELRY;
    const actualProductType =
      productSearchType === ProductCategory.LABGROWN_JEWELRY
        ? ProductCategory.JEWELRY
        : productSearchType;
    const sectionName =
      productSearchType === ProductCategory.LABGROWN_JEWELRY
        ? SearchFilterTypeEnum.LAB_GROWN_JEWELRY
        : getSectionName(
            productSearchType,
            selectedItem.stock_item_sub_category
          );
    dispatch(setProductType(actualProductType));
    dispatch(setSectionName(sectionName));
    DeviceEventEmitter.emit("CLEAR_JEWELRY_CONTEXT");

    const params = {
      productID: selectedItem.id,
      productType: actualProductType,
      navigationRequestId: Date.now(),
      sectionName: sectionName,
      productPrefix:
        productSearchType === ProductCategory.LABGROWN_JEWELRY
          ? ProductCategory.LABGROWN_JEWELRY
          : productPrefix,
      breadCrumbLabel:
        productType !== ProductCategory.JEWELRY &&
        productType !== ProductCategory.LABGROWN_JEWELRY
          ? BreadCrumbLabel.STONE_DETAILS
          : BreadCrumbLabel.SETTING_DETAILS,
    };

    const callback = () => {
      navigate(Routes.GlobalNavigator, {
        screen: Routes.PDPScreen,
        productType: actualProductType,
        initial: true,
        params: params,
      });
      if (enableJewelryBuilder) {
        clearRouteHistory();
        const name = Routes.PDPScreen;
        const mRoute = { name, params };
        addRouteToHistory(mRoute);
        dispatch(setBuilderProduct(productType));
        dispatch(resetFiltersPayload());
      }
    };

    if (shouldConfirmHeaderNavigation(enableJewelryBuilder, history.length)) {
      BuilderAlertManager.open(() => {
        callback();
      });
    } else {
      callback();
    }
  };

  const handleShowSelectAll = (selectedItem: Section) => {
    const callback = () => {
      const [productTypeCategory, subProductTypeCategory] =
        selectedItem?.title.split("_");
      const productSearchType =
        productTypeCategory === "0"
          ? ProductCategory.DIAMOND
          : productTypeCategory === "1"
          ? ProductCategory.LAB_GROWN_DIAMOND
          : productTypeCategory === "2"
          ? ProductCategory.GEMSTONE
          : productTypeCategory === "3"
          ? ProductCategory.JEWELRY
          : ProductCategory.LABGROWN_JEWELRY;
      const actualProductType =
        productSearchType === ProductCategory.LABGROWN_JEWELRY
          ? ProductCategory.JEWELRY
          : productSearchType;
      const sectionName = getSectionName(
        productSearchType,
        subProductTypeCategory
      );
      const params = {
        priceMode: userInfo.getUserPriceMode(),
        productType: actualProductType,
        sectionName,
        productPrefix:
          productSearchType === ProductCategory.LABGROWN_JEWELRY
            ? ProductCategory.LABGROWN_JEWELRY
            : productPrefix,
        breadCrumbLabel:
          actualProductType !== ProductCategory.JEWELRY
            ? BreadCrumbLabel.STONE_DETAILS
            : BreadCrumbLabel.SETTING_DETAILS,
      };
      if (enableJewelryBuilder) {
        clearRouteHistory();
        const name = Routes.PDPScreen;
        const mRoute = { name, params };
        addRouteToHistory(mRoute);
        dispatch(setBuilderProduct(productType));
        dispatch(resetFiltersPayload());
      }
      dispatch(setProductType(actualProductType));
      dispatch(setSectionName(sectionName));
      onRemoveFilters();
      auxPayload = {
        [sectionName.toString()]: {
          cert_num: certNumber,
        },
      };

      let payload = { ...auxPayload };
      if (sectionName?.toLowerCase() === "pairs") {
        payload[sectionName] = {
          ...payload[sectionName],
          pair: StoneType.PAIR,
        };
        dispatch(
          setFiltersPayload({ sectionName, data: payload[sectionName] })
        );
      }
      const tabletOnClick = () => {
        dispatch(setFiltersSet());
        dispatch(setSectionName(sectionName));
      };
      const updatedAuxPayload = handleFilters(sectionName, payload);
      if (!defaultScreenConfig?.params?.saved_search) {
        dispatch(
          setPayloadData({
            data: { ...updatedAuxPayload },
            productType: actualProductType,
          })
        );
      }
      (theme.isLargeScreen || theme.isTablet) && tabletOnClick();
      const mParams = {
        priceMode: userInfo.getUserPriceMode(),
        productType: actualProductType,
        sectionName,
        productPrefix:
          productSearchType === ProductCategory.LABGROWN_JEWELRY
            ? ProductCategory.LABGROWN_JEWELRY
            : productPrefix,
        breadCrumbLabel:
          actualProductType !== ProductCategory.JEWELRY
            ? BreadCrumbLabel.STONE_SEARCH
            : BreadCrumbLabel.SETTING_SEARCH,
        ...(defaultScreenConfig?.params?.saved_search
          ? {
              saved_search: {
                ...JSON.parse(defaultScreenConfig?.params?.saved_search),
                cert_num: certNumber,
              },
            }
          : {}),
      };
      const exploreParams = {
        params: mParams,
        initial: false,
        screen:
          theme.isLargeScreen || theme.isTablet
            ? Routes.FilterSplitScreen
            : Routes.SearchResultScreen,
      };
      if (
        userInfo?.user?.feature_settings
          ?.enable_short_title_filter_in_text_search &&
        selectCollectionFromTextSearch
      ) {
        dispatch(clearSubCollections());
        dispatch(clearCollections());
        dispatch(clearSelectedCollectionOption());
        dispatch(setCollection(selectCollectionFromTextSearch));
      }

      navigate(Routes.AppNavigator, {
        screen: Routes.ExploreNavigator,
        params: exploreParams,
      });
      if (enableJewelryBuilder) {
        clearRouteHistory();
        const name =
          theme.isLargeScreen || theme.isTablet
            ? Routes.FilterSplitScreen
            : Routes.SearchResultScreen;
        const mRoute = { name, params: mParams };
        addRouteToHistory(mRoute);
        dispatch(setBuilderProduct(productType));
      }
    };
    if (shouldConfirmHeaderNavigation(enableJewelryBuilder, history.length)) {
      BuilderAlertManager.open(() => {
        callback();
      });
    } else {
      callback();
    }
  };

  const handleBrandPress = (brand: Brand) => {
    const sectionName = getSectionName(ProductCategory.JEWELRY);
    const productSearchType = ProductCategory.JEWELRY;
    const params = {
      productID: brand.id,
      productType: productSearchType,
      navigationRequestId: Date.now(),
      sectionName: sectionName,
      productPrefix: productPrefix,
      breadCrumbLabel:
        productSearchType !== ProductCategory.JEWELRY &&
        productSearchType !== ProductCategory.LABGROWN_JEWELRY
          ? BreadCrumbLabel.STONE_DETAILS
          : BreadCrumbLabel.SETTING_DETAILS,
    };
    dispatch(setProductType(productSearchType));
    dispatch(setSectionName(sectionName));
    DeviceEventEmitter.emit("CLEAR_JEWELRY_CONTEXT");

    const callback = () => {
      navigate(Routes.GlobalNavigator, {
        screen: Routes.PDPScreen,
        productType: productSearchType,
        initial: true,
        params: params,
      });
      if (enableJewelryBuilder) {
        clearRouteHistory();
        const name = Routes.PDPScreen;
        const mRoute = { name, params };
        addRouteToHistory(mRoute);
        dispatch(setBuilderProduct(productSearchType));
        dispatch(resetFiltersPayload());
      }
    };

    if (shouldConfirmHeaderNavigation(enableJewelryBuilder, history.length)) {
      BuilderAlertManager.open(() => {
        callback();
      });
    } else {
      callback();
    }
  };

  const certNumber = useMemo(() => {
    const trimmedValue = debouncedInputValue.trim();
    const lowerCaseValue = trimmedValue.toLowerCase();
    const normaliseMultipleCert = lowerCaseValue.split(",");
    const numericPart =
      normaliseMultipleCert.length > 1
        ? normaliseMultipleCert
            .map((item) => item.trim().replace(/^vdb-?/i, ""))
            .join(",")
        : lowerCaseValue.startsWith("vdb-")
        ? trimmedValue.replace(/^VDB-/i, "")
        : lowerCaseValue.startsWith("vdb")
        ? trimmedValue.replace(/^VDB/i, "")
        : trimmedValue;
    return numericPart;
  }, [debouncedInputValue]);

  const [brandSearch, setBrandSearch] = useState("");
  const [debouncedBrandSearch] = useDebounce(brandSearch, 300);

  // Convert vdb_setting to boolean
  const vdbSettingBool = userInfo?.user?.vdb_setting === "true";

  // Fetch brand list only once
  const { data: allBrands = [], isLoading: brandsLoading } = useBrandsList(
    selectedGroups,
    vdbSettingBool,
    {
      enabled: jewelryWithId,
    }
  );

  type Brand = {
    id: string | number;
    label: string;
  };

  const filteredBrands = useMemo(() => {
    if (!Array.isArray(allBrands)) return [];

    const search = debouncedBrandSearch.toLowerCase();

    const filtered = search
      ? allBrands.filter((brand: Brand) =>
          (brand.label ?? "").toLowerCase()?.includes(search ?? "")
        )
      : allBrands;

    return [...filtered].sort((a: Brand, b: Brand) => {
      const aLabel = (a.label ?? "").toLowerCase();
      const bLabel = (b.label ?? "").toLowerCase();

      // 1️⃣ Starts-with search text comes first
      const aStarts = aLabel.startsWith(search);
      const bStarts = bLabel.startsWith(search);

      if (aStarts !== bStarts) {
        return aStarts ? -1 : 1;
      }

      // 2️⃣ Alphabetical order
      const alphaCompare = aLabel.localeCompare(bLabel, undefined, {
        sensitivity: "base",
      });
      if (alphaCompare !== 0) return alphaCompare;

      // 3️⃣ Numeric ordering (PBC2 < PBC25 < PBC100)
      const aNum = parseInt(aLabel.replace(/\D+/g, ""), 10) || 0;
      const bNum = parseInt(bLabel.replace(/\D+/g, ""), 10) || 0;

      return aNum - bNum;
    });
  }, [debouncedBrandSearch, allBrands]);

  const searchTextResultApiCall = useTextSearchResult(
    1,
    !jewelryWithId && certNumber.trim().length >= 3,
    50,
    userInfo?.user?.price_mode ?? PriceModes.WHITE,
    certNumber,
    productType === "" ? undefined : productType,
    finalResult,
    productSubType
  );

  const dataCount = useMemo(() => {
    return data?.reduce((acc, item) => acc + item.data.length, 0) || 0;
  }, [data]);

  const isLoading = useMemo(() => {
    return (
      searchTextResultApiCall.isInitialLoading ||
      searchTextResultApiCall.isFetching
    );
  }, [searchTextResultApiCall]);

  const shouldShowNoRecentSearch = useMemo(
    () =>
      (!inputValue.toLowerCase().startsWith("vdb" && "vdb-") &&
        inputValue.length < 3) ||
      (inputValue.toLowerCase().startsWith("vdb" || "vdb-") &&
        inputValue.length <= 6),
    [inputValue]
  );

  const shouldShowNoRecentBrands = useMemo(
    () => inputValue.length < 1,
    [inputValue]
  );

  const shouldShowNoMatchesFound = useMemo(
    () =>
      (!searchTextResultApiCall?.data?.data?.data ||
        searchTextResultApiCall?.data?.data?.data.length === 0) &&
      !isLoading &&
      searchTextResultApiCall.isFetched,
    [
      searchTextResultApiCall?.data?.data?.data,
      isLoading,
      searchTextResultApiCall.isFetched,
    ]
  );

  useEffect(() => {
    if (customWidth) {
      setSearchWidth(extractWidthValue(customWidth));
    }
  }, []);

  useEffect(() => {
    navigation.setOptions({
      contentStyle: {
        borderTopColor: null,
        borderTopWidth: 0,
      },
      headerStyle: {
        borderBottomColor: null,
        borderBottomWidth: 0,
        backgroundColor: theme["core-02"],
      },
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 500);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const handleSearch = (value: string) => {
    if (jewelryWithId) {
      // Brand search mode
      setBrandSearch(value);
    } else {
      // Cert search mode
      if (value.length <= 2) {
        setData([]);
      }
    }
    setInputValue(value);
  };

  useEffect(() => {
    if (
      searchTextResultApiCall.isSuccess &&
      searchTextResultApiCall.isFetched &&
      !searchTextResultApiCall.isFetching &&
      searchTextResultApiCall.data
    ) {
      const newData = searchTextResultApiCall.data.data;
      const allowedCategories = userInfo?.getAllowedStockTypes() || [];
      const filteredCategories = hasLabGrownJewelry(
        allowedProducts,
        userInfo?.getPriceMode()
      )
        ? [...allowedCategories, ProductCategory.LABGROWN_JEWELRY]
        : allowedCategories;
      if (newData.data) {
        setShowAll(newData.data.length > 1);

        const filteredItems = newData.data.filter((item) => {
          const category = consolidatedStoneTypes?.enabled
            ? productTypeToCategory[`${item.product}_single`]
            : productTypeToCategory[
                `${item.product}_${item.stock_item_sub_category}`
              ];
          return filteredCategories.some((cat) => category.includes(cat));
        });

        const filteredData = Object.entries(productTypeToCategory)
          .map(([key, category]) => {
            const typeData = filteredItems.filter((item) => {
              return (
                (consolidatedStoneTypes?.enabled
                  ? `${item.product}_single`
                  : `${item.product}_${item.stock_item_sub_category}`) ===
                  key && newData?.counts[category] > 0
              );
            });
            if (typeData.length === 0) return null;

            const counts = newData.counts as
              | { [key: string]: number }
              | undefined;
            const count = counts?.[category] ?? 0;

            return {
              title: key,
              data: typeData,
              showAll: count > 1,
              message: t("textSearchScreen.showAllResults.text", {
                count: commaFormattedValue(String(count), true),
              }),
              description: t(
                "textSearchScreen.showAllResults.description.text"
              ),
            };
          })
          .filter(Boolean);

        setData(filteredData);
      }
    }
  }, [
    searchTextResultApiCall.isSuccess,
    searchTextResultApiCall.isFetched,
    searchTextResultApiCall.isFetching,
    searchTextResultApiCall.data,
  ]);

  const detailsView = useMemo(() => {
    // LOADING
    if (jewelryWithId && brandsLoading) return <Loader />;
    if (!jewelryWithId && isLoading) return <Loader />;

    // BRANDS MODE
    if (jewelryWithId) {
      if (shouldShowNoRecentBrands) {
        return <NoRecentSearch t={t} typography={typography} theme={theme} />;
      }
      if (filteredBrands.length === 0) {
        return (
          <NoMatchesFound
            handleOnPressExploreProducts={handleOnPressExploreProducts}
            t={t}
            typography={typography}
            theme={theme}
          />
        );
      }

      return (
        <ScrollView style={{ padding: 16 }}>
          <TextSearchProductType
            productId={"3"}
            showResultsAsTooltip={showResultsAsTooltip}
            noPadding
          />
          {filteredBrands.map((brand: Brand, index: number) => (
            <>
              <Pressable
                key={brand.id}
                onPress={() => handleBrandPress(brand)}
                onMouseEnter={() => setFocusedItem(index)}
                style={{
                  paddingVertical: 16,
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <VDBText
                  color={
                    index === focussedItem
                      ? theme["link-hover"]
                      : theme["core-05"]
                  }
                  type={typography["Body-R"]}
                >
                  {brand.label}
                </VDBText>
                <VDBIcon
                  color={
                    index === focussedItem ? theme["core-06"] : theme["core-04"]
                  }
                  size={SpaceType.Big}
                  variant={IconsVariant.VDBCustom}
                  name={VDBIcons.ARROW_RIGHT}
                />
              </Pressable>
              {index !== filteredBrands.length - 1 && (
                <LineViewContainer
                  background={index === focussedItem ? "core-04" : "core-03"}
                />
              )}
            </>
          ))}
        </ScrollView>
      );
    }

    // CERT MODE
    if (!jewelryWithId) {
      if (shouldShowNoRecentSearch) {
        return <NoRecentSearch t={t} typography={typography} theme={theme} />;
      }

      if (shouldShowNoMatchesFound) {
        return (
          <NoMatchesFound
            handleOnPressExploreProducts={handleOnPressExploreProducts}
            t={t}
            typography={typography}
            theme={theme}
          />
        );
      }

      return (
        <SearchResults
          data={data || []}
          showResultsAsTooltip={showResultsAsTooltip}
          dataCount={dataCount}
          maxRecords={NUMBER_OF_RECORDS_TO_AGGREGATE}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onSelectItem={handleDidSelectItem}
          isCustomApp={isCustomApp}
          onSelectShowAll={handleShowSelectAll}
          showAll={showAll}
          enableShortTitleFilterInTextSearch={
            userInfo?.user?.feature_settings
              ?.enable_short_title_filter_in_text_search
          }
          focussedItem={focussedItem}
          setFocusedItem={setFocusedItem}
        />
      );
    }
  }, [
    jewelryWithId,
    isLoading,
    brandsLoading,
    filteredBrands,
    shouldShowNoRecentSearch,
    shouldShowNoMatchesFound,
    shouldShowNoRecentBrands,
    data,
    focussedItem,
  ]);

  const onBlurInput = (event: any, onBlur?: () => void) => {
    if (showResultsAsTooltip) {
      if (event?.nativeEvent?.relatedTarget !== null || document.hasFocus()) {
        setTimeout(() => handleLeftIconPress(false), 300);
      }
    }
    onBlur?.();
  };

  const enableShortTitle =
    userInfo?.user?.feature_settings?.enable_short_title_filter_in_text_search;

  const getVisibleCount = (section: Section) => {
    if (section.showAll) {
      // Match SearchResults: up to 5 items + Show All CTA for short-title exact match
      if (enableShortTitle) return Math.min(5, section.data.length) + 1;
      return 1;
    }
    if (dataCount >= NUMBER_OF_RECORDS_TO_AGGREGATE) return 0;
    return section.data.length;
  };

  const totalItems = useMemo(() => {
    return (data || []).reduce(
      (acc, section) => acc + getVisibleCount(section),
      0
    );
  }, [data, enableShortTitle, dataCount]);

  const resolveFlatIndex = (
    flatIndex: number
  ): { section: Section; itemIndex: number } | null => {
    let remaining = flatIndex;
    for (const section of data || []) {
      const count = getVisibleCount(section);
      if (remaining < count) {
        return { section, itemIndex: remaining };
      }
      remaining -= count;
    }
    return null;
  };

  const handleEnterOnFocusedItem = () => {
    const resolved = resolveFlatIndex(focussedItem);
    if (!resolved) return;

    const { section, itemIndex } = resolved;

    if (section.showAll) {
      const showAllRowIndex = enableShortTitle
        ? Math.min(5, section.data.length)
        : 0;
      const isShowAllRow = itemIndex === showAllRowIndex;
      if (isShowAllRow) {
        handleShowSelectAll(section);
      } else {
        handleDidSelectItem(section.data[itemIndex]);
      }
    } else {
      handleDidSelectItem(section.data[itemIndex]);
    }
    handleLeftIconPress(true);
  };

  const handleKeyPress = (event: KeyboardEvent, isSubmitEditing: boolean) => {
    const key = event.key ?? event.code;
    if (key === "ArrowDown") {
      event.preventDefault?.();
      if (jewelryWithId) {
        if (focussedItem === filteredBrands.length - 1) {
          setFocusedItem(0);
        } else {
          setFocusedItem((prev) =>
            Math.min(prev + 1, filteredBrands.length - 1)
          );
        }
      } else {
        if (focussedItem === totalItems - 1) {
          setFocusedItem(0);
        } else {
          setFocusedItem((prev) => Math.min(prev + 1, totalItems - 1));
        }
      }
      return;
    }
    if (key === "ArrowUp") {
      event.preventDefault?.();
      if (jewelryWithId) {
        if (focussedItem === 0) {
          setFocusedItem(filteredBrands?.length - 1);
        } else {
          setFocusedItem((prev) => Math.max(prev - 1, 0));
        }
      } else {
        if (focussedItem === 0) {
          setFocusedItem(totalItems - 1);
        } else {
          setFocusedItem((prev) => Math.max(prev - 1, 0));
        }
      }
      return;
    }
    if (key === "Enter" || (Platform.OS !== "web" && isSubmitEditing)) {
      event.preventDefault?.();
      if (jewelryWithId) {
        if (filteredBrands.length === 1) {
          handleLeftIconPress(true);
          handleBrandPress(filteredBrands[0]);
          return false;
        } else {
          handleDidSelectItem(filteredBrands?.[focussedItem]);
        }
      }
      if (!jewelryWithId && data && data.length > 0) {
        handleEnterOnFocusedItem();
      }
      if (shouldShowNoMatchesFound) {
        handleOnPressExploreProducts();
      }
      return false;
    }
  };

  const getInputComponent = () => {
    return (
      <InputWrapper applyPaddingRight={!disableBarcodeScanner}>
        <InputContainer>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur } }) => (
              <Input
                inputRef={inputRef}
                inputLabel={""}
                placeholder={""}
                searchPlaceholder={t("textSearchScreen.input.placeholder.text")}
                returnKeyType="search"
                leftIcon={
                  theme.isLargeScreen || inputValue.length > 0
                    ? VDBIcons.CLOSE_CIRCLE
                    : VDBIcons.LEFT
                }
                rightIcon={VDBIcons.MAIN_SEARCH}
                hideIconContainer={false}
                onBlur={(event) => onBlurInput(event, onBlur)}
                onKeyPress={(e) => handleKeyPress(e, false)}
                value={jewelryWithId ? brandSearch : inputValue}
                isHideBorder={!theme.isLargeScreen}
                isShownTopPadding={true}
                onLeftIconPress={() => handleLeftIconPress(true)}
                onSubmitEditing={(e) => handleKeyPress(e, true)}
                disableLeftIcon={false}
                onChangeText={(value) => {
                  let finalValue = value;
                  if (
                    jewelryWithId ||
                    userInfo?.user?.feature_settings
                      ?.enable_short_title_filter_in_text_search ||
                    userInfo?.allowSpaceInGlobalTextSearchFor20()
                  ) {
                    finalValue = value;
                  } else {
                    finalValue = value
                      .replace(/\s+/g, ",")
                      .replace(/,+/g, ",")
                      .replace(/^,/, "");
                  }
                  const regex = /^[\s\S]*$/;
                  if (regex.test(finalValue) && finalValue[0] !== " ") {
                    onChange(finalValue);
                    handleSearch(finalValue);
                  }
                }}
                leftIconColor={
                  inputValue.length === 0 ? theme["core-05"] : theme["core-04"]
                }
              />
            )}
            name="collectionName"
          />
        </InputContainer>
        {!disableBarcodeScanner && <BarcodeButton />}
      </InputWrapper>
    );
  };

  const getInput = () => {
    return (
      <View
        style={{
          backgroundColor: theme.transparent,
          borderRadius: SpaceType.Basic,
        }}
      >
        {showResultsAsTooltip ? (
          <Tooltip
            isOpen={
              jewelryWithId
                ? debouncedBrandSearch.length >= 1 // show brands immediately
                : debouncedInputValue.length >= 3 // cert mode requires 3 chars
            }
            placement="bottom left"
            trigger={(triggerProps) => {
              return (
                <View
                  {...triggerProps}
                  style={{ width: searchWidth ? searchWidth : MAX_WIDTH }}
                >
                  {getInputComponent()}
                </View>
              );
            }}
          >
            <TooltipContent
              style={{
                margin: 0,
                padding: 0,
                paddingBottom: 16,
                borderRadius: SpaceType.Basic,
                backgroundColor: theme["core-02"],
                maxHeight: 600,
                minHeight: 300,
                width: searchWidth ? searchWidth : MAX_WIDTH,
                overflow: "hidden",
              }}
            >
              <AppProviders>{detailsView}</AppProviders>
            </TooltipContent>
          </Tooltip>
        ) : (
          getInputComponent()
        )}
      </View>
    );
  };

  return (
    <>
      <SafeAreaView
        edges={["top"]}
        style={{
          backgroundColor: theme["core-01"],
          borderRadius: SpaceType.Basic,
        }}
      >
        {getInput()}
      </SafeAreaView>
      {!showResultsAsTooltip && (
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={{ flex: 1, backgroundColor: theme["core-02"] }}
        >
          {detailsView}
        </KeyboardAvoidingView>
      )}
    </>
  );
};

const InputWrapper = styled.View<{ applyPaddingRight: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-right: ${({ applyPaddingRight }: { applyPaddingRight: boolean }) =>
    applyPaddingRight ? 16 : 0}px;
`;

const InputContainer = styled.View`
  flex: 1;
`;

export default TextSearchScreen;
